// File: frontend/src/components/ResponseModal.tsx
import React, { useState, useEffect } from 'react';
import { X, MessageSquare, User, Star, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface InterviewQuestion {
  id: number;
  category: string;
  priority: number;
  question_text: string;
  follow_up_questions: string[];
  gap_reference: string;
  estimated_time: number;
  notes?: string;
  answer?: string;
  created_at: string;
}

interface ResponseModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: InterviewQuestion;
  onSave: (responseData: {
    question_id: number;
    response_text: string;
    respondent_name: string;
    confidence_impact: number;
    answer?: string;
    notes?: string;
  }) => Promise<void>;
}

const ResponseModal: React.FC<ResponseModalProps> = ({
  isOpen,
  onClose,
  question,
  onSave
}) => {
  const [responseText, setResponseText] = useState('');
  const [respondentName, setRespondentName] = useState('');
  const [confidenceImpact, setConfidenceImpact] = useState(3);
  const [saving, setSaving] = useState(false);
  const [answer, setAnswer] = useState('');
  const [notes, setNotes] = useState('');


  // Reset form when modal opens/closes
  useEffect(() => {
  if (isOpen && question) {
    setResponseText('');
    setRespondentName('');
    setConfidenceImpact(3);
    setAnswer(question.answer || '');
    setNotes(question.notes || '');
  }
}, [isOpen, question?.id]);

  const handleSave = async () => {
    if (!responseText.trim()) {
      toast.error('Please provide a response');
      return;
    }

    if (!respondentName.trim()) {
      toast.error('Please provide respondent name');
      return;
    }

    try {
      setSaving(true);
      
     await onSave({
      question_id: question.id,
      response_text: responseText.trim(),
      respondent_name: respondentName.trim(),
      confidence_impact: confidenceImpact,
      answer: responseText.trim(), // Use responseText as answer for auto-update trigger
      notes: notes.trim()
    });

      toast.success('Response saved successfully');
      onClose();
    } catch (error: any) {
      console.error('Failed to save response:', error);
      toast.error('Failed to save response');
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getPriorityBadge = (priority: number) => {
    switch (priority) {
      case 1:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            High Priority
          </span>
        );
      case 2:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Medium Priority
          </span>
        );
      case 3:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Low Priority
          </span>
        );
      default:
        return null;
    }
  };

  const getConfidenceDescription = (impact: number) => {
    switch (impact) {
      case 1:
        return 'Very Low - Response provides minimal clarity or confidence';
      case 2:
        return 'Low - Response provides some information but significant gaps remain';
      case 3:
        return 'Medium - Response provides moderate clarity and fills some gaps';
      case 4:
        return 'High - Response provides significant clarity and fills major gaps';
      case 5:
        return 'Very High - Response completely resolves uncertainty and fills all gaps';
      default:
        return '';
    }
  };

  const getImpactColor = (impact: number) => {
    switch (impact) {
      case 1:
      case 2:
        return 'text-red-600';
      case 3:
        return 'text-yellow-600';
      case 4:
      case 5:
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  if (!isOpen || !question) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      onClick={handleBackdropClick}
    >
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 xl:w-2/5 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start border-b pb-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Capture Interview Response</h3>
            </div>
            <div className="flex items-center gap-2">
              {getPriorityBadge(question.priority)}
              <span className="text-sm text-gray-500">
                {question.category} • {new Date().toLocaleDateString()}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={saving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Question Display */}
        <div className="mb-6">
          <h4 className="font-medium text-gray-900 mb-3">Interview Question:</h4>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-800 leading-relaxed">{question.question_text}</p>
          </div>
          
          {question.follow_up_questions && question.follow_up_questions.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium text-gray-800 mb-2 text-sm">Follow-up Questions:</h5>
              <ul className="space-y-1 ml-4">
                {question.follow_up_questions.map((followUp, idx) => (
                  <li key={idx} className="text-gray-600 text-sm">
                    • {followUp}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {question.gap_reference && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800 font-medium mb-1">Gap Reference:</p>
              <p className="text-sm text-amber-700">{question.gap_reference}</p>
            </div>
          )}
        </div>

        {/* Response Form */}
        <div className="space-y-6">
          {/* Respondent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Respondent Name * <span className='text-xs text-green-500'>(Subject Matter Expert Who Reviewed)</span>
            </label>
            <input
              type="text"
              value={respondentName}
              onChange={(e) => setRespondentName(e.target.value)}
              placeholder="Enter the name of the person providing this response"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              disabled={saving}
            />
          </div>

          {/* Response Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Response *
            </label>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter the detailed response to this interview question..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 resize-vertical"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Provide as much detail as possible to ensure accurate process understanding.
            </p>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Context
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes or observations..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 resize-vertical"
              disabled={saving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Additional context, observations, or follow-up items.
            </p>
          </div>

          {/* Confidence Impact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Star className="w-4 h-4 inline mr-1" />
              Confidence Impact Rating *
            </label>
            <div className="space-y-3">
              {/* Rating Scale */}
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <label key={rating} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="confidence"
                      value={rating}
                      checked={confidenceImpact === rating}
                      onChange={(e) => setConfidenceImpact(parseInt(e.target.value))}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                      disabled={saving}
                    />
                    <span className={`font-medium ${getImpactColor(rating)}`}>
                      {rating}
                    </span>
                  </label>
                ))}
              </div>
              
              {/* Scale Labels */}
              <div className="flex justify-between text-xs text-gray-500">
                <span>Very Low</span>
                <span>Low</span>
                <span>Medium</span>
                <span>High</span>
                <span>Very High</span>
              </div>
              
              {/* Current Selection Description */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className={`text-sm font-medium ${getImpactColor(confidenceImpact)}`}>
                  Impact Level {confidenceImpact}: {getConfidenceDescription(confidenceImpact)}
                </p>
              </div>
              
              {/* High Impact Warning */}
              {confidenceImpact >= 4 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">High Impact Response</p>
                      <p className="text-sm text-green-700">
                        This response will automatically trigger an update to the process synthesis 
                        to incorporate the new high-confidence information.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-6 border-t mt-8">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !responseText.trim() || !respondentName.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Response
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResponseModal;